// src/components/dashboards/DailyExpensesTab.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { expenseAPI } from '../../services/api';
import { notify, confirmDialog } from '../../utils/notify';

const getLocalDateString = () => new Date().toLocaleDateString('en-CA');
const localNow = new Date();

const CATEGORY_META = {
  ELECTRICITY:       { label: 'Electricity & Utilities', color: '#f59e0b', icon: '⚡' },
  LAB_EQUIPMENT:     { label: 'Lab Equipment',           color: '#6366f1', icon: '🔬' },
  SALARIES:          { label: 'Salaries & Wages',        color: '#10b981', icon: '💼' },
  MAINTENANCE:       { label: 'Maintenance & Repair',    color: '#f97316', icon: '🔧' },
  TRANSPORT:         { label: 'Transport',               color: '#3b82f6', icon: '🚌' },
  LIBRARY:           { label: 'Library Resources',       color: '#8b5cf6', icon: '📚' },
  HOSTEL:            { label: 'Hostel Operations',       color: '#ec4899', icon: '🏠' },
  MEDICAL_SUPPLIES:  { label: 'Medical Supplies',        color: '#14b8a6', icon: '💊' },
  STATIONERY:        { label: 'Stationery & Office',     color: '#64748b', icon: '✏️' },
  CANTEEN:           { label: 'Canteen & Catering',      color: '#d97706', icon: '🍽️' },
  IT_INFRASTRUCTURE: { label: 'IT & Infrastructure',     color: '#0ea5e9', icon: '💻' },
  MISCELLANEOUS:     { label: 'Miscellaneous',           color: '#9ca3af', icon: '📦' },
};

const PAYMENT_METHODS = ['CASH', 'CHEQUE', 'BANK_TRANSFER', 'UPI', 'CARD'];
const MONTH_NAMES      = ['','January','February','March','April','May','June',
  'July','August','September','October','November','December'];

const fmt = (n) =>
  `₹${parseFloat(n||0).toLocaleString('en-IN',{minimumFractionDigits:2,maximumFractionDigits:2})}`;
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—';

const statusPill = (s) => {
  const map = { APPROVED:['#d1fae5','#065f46'], PENDING:['#fef3c7','#92400e'], REJECTED:['#fee2e2','#991b1b'] };
  const [bg,fg] = map[s]||['#f3f4f6','#374151'];
  return <span style={{display:'inline-block',padding:'2px 10px',borderRadius:9999,background:bg,color:fg,fontSize:11,fontWeight:700,letterSpacing:'0.04em',textTransform:'uppercase'}}>{s}</span>;
};

/* ─── Responsive CSS ─── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=DM+Mono:wght@400;500&family=DM+Sans:wght@400;500;600;700&display=swap');
*{box-sizing:border-box}
@keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
@keyframes slideUp{from{opacity:0;transform:translateY(32px)}to{opacity:1;transform:translateY(0)}}
@keyframes spin{to{transform:rotate(360deg)}}
@keyframes shimmer{to{background-position:-200% 0}}
.fade-in{animation:fadeIn 0.3s ease}
.slide-up{animation:slideUp 0.28s cubic-bezier(.22,.68,0,1.2)}
.spin{animation:spin 0.8s linear infinite;display:inline-block}
.exp-row:hover td{background:#f8fafc !important}
.exp-row td{transition:background 0.15s}
.btn-hover:hover{opacity:0.88;transform:translateY(-1px);transition:all 0.15s}
input:focus,select:focus,textarea:focus{outline:2px solid #3b82f6;outline-offset:1px}
::-webkit-scrollbar{width:6px;height:6px}
::-webkit-scrollbar-track{background:#f1f5f9}
::-webkit-scrollbar-thumb{background:#cbd5e1;border-radius:3px}

/* Summary strip */
.exp-summary-strip{display:flex;margin-bottom:20px}

/* Header row */
.exp-header-row{display:flex;justify-content:space-between;align-items:flex-end;margin-bottom:16px;gap:12px;flex-wrap:wrap}
.exp-header-actions{display:flex;gap:10px;flex-wrap:wrap;align-items:center}

/* Filter bar */
.exp-filter-bar{display:flex;flex-wrap:wrap;gap:8px;margin-bottom:10px;align-items:center}
.exp-filter-input{padding:8px 12px;border-radius:8px;border:1px solid #e2e8f0;font-size:13px;background:#fff;color:#374151;flex:1;min-width:140px}
.exp-filter-select{padding:8px 10px;border-radius:8px;border:1px solid #e2e8f0;font-size:13px;background:#fff;color:#374151}

/* Table vs cards */
.exp-table-wrap{display:block;overflow-x:auto;border-radius:12px;box-shadow:0 1px 6px rgba(0,0,0,0.08);border:1px solid #e2e8f0;background:#fff}
.exp-mobile-cards{display:none}

/* Mobile card */
.exp-mobile-card{background:#fff;border-radius:12px;border:1px solid #e2e8f0;padding:14px 16px;margin-bottom:10px;box-shadow:0 1px 4px rgba(0,0,0,0.06)}
.exp-mobile-card-header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:10px;gap:8px}
.exp-mobile-card-body{display:grid;grid-template-columns:1fr 1fr;gap:8px 16px;margin-bottom:10px}
.exp-mobile-field-label{font-size:10px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:0.06em;margin-bottom:2px}
.exp-mobile-field-value{font-size:13px;color:#1e293b;font-weight:500}
.exp-mobile-card-footer{display:flex;justify-content:space-between;align-items:center;padding-top:10px;border-top:1px solid #f1f5f9;gap:8px}

/* Form row */
.exp-form-row{display:flex;gap:12px}

/* Modal */
.exp-modal-box{background:#fff;border-radius:14px;width:100%;max-height:92vh;overflow:hidden;box-shadow:0 24px 64px rgba(0,0,0,0.22);display:flex;flex-direction:column;z-index:10000}

/* Month selectors */
.exp-month-row{display:flex;gap:8px;margin-bottom:8px}

/* ── Tablet (≤900px) ── */
@media(max-width:900px){
  .exp-summary-strip{grid-template-columns:repeat(2,1fr)}
}

/* ── Mobile (≤640px) ── */
@media(max-width:640px){
  .exp-summary-strip{grid-template-columns:repeat(2,1fr);gap:10px}
  .exp-header-row{flex-direction:column;align-items:stretch}
  .exp-header-actions{justify-content:stretch}
  .exp-header-actions button{flex:1;justify-content:center;font-size:12px !important;padding:8px 8px !important}
  .exp-filter-input{min-width:100%}
  .exp-filter-select{width:100%}
  .exp-table-wrap{display:none !important}
  .exp-mobile-cards{display:block !important}
  .exp-form-row{flex-direction:column;gap:0}
  .exp-modal-box{border-radius:16px 16px 0 0 !important;position:fixed !important;bottom:0 !important;left:0 !important;right:0 !important;width:100% !important;max-width:100% !important;max-height:95vh !important}
}

/* ── Very small (≤400px) ── */
@media(max-width:400px){
  .exp-summary-strip{grid-template-columns:1fr}
  .exp-mobile-card-body{grid-template-columns:1fr}
}
`;

/* ─── Report Generator ─── */
const generateReport = ({ expenses, summary, categoryStats, viewMonth, viewYear, filters }) => {
  const reportTitle = `Daily Expense Report — ${MONTH_NAMES[viewMonth]} ${viewYear}`;
  const generatedOn = new Date().toLocaleString('en-IN',{day:'2-digit',month:'long',year:'numeric',hour:'2-digit',minute:'2-digit',hour12:true});
  const totalAmount = expenses.reduce((s,e)=>s+parseFloat(e.amount||0),0);
  const approvedAmt = expenses.filter(e=>e.status==='APPROVED').reduce((s,e)=>s+parseFloat(e.amount||0),0);
  const pendingAmt  = expenses.filter(e=>e.status==='PENDING').reduce((s,e)=>s+parseFloat(e.amount||0),0);
  const rejectedAmt = expenses.filter(e=>e.status==='REJECTED').reduce((s,e)=>s+parseFloat(e.amount||0),0);
  const approvedCnt = expenses.filter(e=>e.status==='APPROVED').length;
  const pendingCnt  = expenses.filter(e=>e.status==='PENDING').length;
  const rejectedCnt = expenses.filter(e=>e.status==='REJECTED').length;
  const catMap={};
  expenses.forEach(e=>{if(!catMap[e.category])catMap[e.category]={total:0,count:0,display:e.category_display};catMap[e.category].total+=parseFloat(e.amount||0);catMap[e.category].count+=1;});
  const catRows=Object.entries(catMap).sort((a,b)=>b[1].total-a[1].total).map(([cat,d])=>{
    const pct=totalAmount>0?((d.total/totalAmount)*100).toFixed(1):'0.0';
    const color=CATEGORY_META[cat]?.color||'#6b7280';const icon=CATEGORY_META[cat]?.icon||'📦';
    return `<tr><td><span style="margin-right:6px">${icon}</span>${d.display}</td><td style="text-align:right">${d.count}</td><td style="text-align:right;font-family:monospace;font-weight:700">${d.total.toLocaleString('en-IN',{style:'currency',currency:'INR',minimumFractionDigits:2})}</td><td style="text-align:right"><div style="display:flex;align-items:center;gap:8px;justify-content:flex-end"><div style="width:80px;height:7px;background:#e5e7eb;border-radius:4px;overflow:hidden"><div style="width:${pct}%;height:100%;background:${color};border-radius:4px"></div></div><span style="min-width:36px;text-align:right">${pct}%</span></div></td></tr>`;
  }).join('');
  const expRows=expenses.map((e,i)=>{
    const sc={APPROVED:{bg:'#d1fae5',fg:'#065f46'},PENDING:{bg:'#fef3c7',fg:'#92400e'},REJECTED:{bg:'#fee2e2',fg:'#991b1b'}}[e.status]||{bg:'#f3f4f6',fg:'#374151'};
    const meta=CATEGORY_META[e.category]||{};
    const amt=parseFloat(e.amount||0).toLocaleString('en-IN',{style:'currency',currency:'INR',minimumFractionDigits:2});
    return `<tr style="background:${i%2===0?'#fff':'#f9fafb'}"><td style="text-align:center;color:#6b7280;font-size:12px">${i+1}</td><td style="font-family:monospace;font-size:12px">${fmtDate(e.date)}</td><td><span style="background:${(meta.color||'#6b7280')+'20'};color:${meta.color||'#6b7280'};padding:2px 8px;border-radius:4px;font-size:11px;font-weight:700">${meta.icon||''} ${e.category_display||e.category}</span></td><td><div style="font-weight:600;font-size:12px">${e.description||'—'}</div>${e.vendor?`<div style="font-size:11px;color:#9ca3af">🏢 ${e.vendor}</div>`:''}</td><td style="font-family:monospace;font-size:11px;color:#6b7280">${e.invoice_number||'—'}</td><td style="font-size:12px">${e.payment_method_display||'—'}</td><td style="text-align:right;font-family:monospace;font-weight:700;color:#dc2626;font-size:13px">${amt}</td><td style="font-size:11px;color:#6b7280">${e.created_by_name||'—'}</td><td style="font-size:11px;color:#6b7280;max-width:140px"><div style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:140px">${e.notes||'—'}</div></td></tr>`;
  }).join('');
  const filterLines=[];
  if(filters.catFilter) filterLines.push(`Category: ${CATEGORY_META[filters.catFilter]?.label||filters.catFilter}`);
  if(filters.statusFilter) filterLines.push(`Status: ${filters.statusFilter}`);
  if(filters.startDate) filterLines.push(`From: ${fmtDate(filters.startDate)}`);
  if(filters.endDate)   filterLines.push(`To: ${fmtDate(filters.endDate)}`);
  if(filters.search)    filterLines.push(`Search: "${filters.search}"`);

  const html=`<!DOCTYPE html><html><head><meta charset="UTF-8"/><title>${reportTitle}</title>
  <style>
  @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=DM+Mono:wght@400;500&family=DM+Sans:wght@400;500;600;700&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'DM Sans',sans-serif;background:#f8fafc;color:#1e293b;font-size:13px}
  .page{max-width:1200px;margin:0 auto;padding:32px 40px}
  .rh{background:#0f172a;border-radius:16px;padding:32px 40px;margin-bottom:28px;display:flex;justify-content:space-between;align-items:flex-start;gap:24px}
  .brand{display:flex;align-items:center;gap:10px;margin-bottom:12px}
  .bd{width:36px;height:36px;background:linear-gradient(135deg,#6366f1,#3b82f6);border-radius:10px;display:flex;align-items:center;justify-content:center;font-size:18px}
  .bn{color:#e2e8f0;font-size:16px;font-weight:700}
  .rt{font-family:'DM Serif Display',serif;font-size:28px;font-weight:400;color:#fff;margin-bottom:8px}
  .rm{color:#64748b;font-size:12px;line-height:1.8} .rm span{color:#94a3b8}
  .rb{background:#1e3a5f;border:1px solid #3b82f6;border-radius:8px;padding:12px 18px;color:#93c5fd;font-size:12px;font-weight:600}
  .rb .ta{font-family:'DM Mono',monospace;font-size:22px;font-weight:700;color:#fff;display:block;margin-top:4px}
  .fi{background:#fffbeb;border:1px solid #fde68a;border-radius:10px;padding:12px 16px;margin-bottom:24px;font-size:12px;color:#92400e;display:flex;align-items:center;gap:8px;flex-wrap:wrap}
  .ft{background:#fef3c7;padding:3px 10px;border-radius:12px;font-weight:600}
  .sg{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:28px}
  .sc{background:#fff;border-radius:12px;padding:18px 20px;box-shadow:0 1px 4px rgba(0,0,0,0.07);border:1px solid #f1f5f9}
  .sl{font-size:10px;font-weight:700;color:#94a3b8;letter-spacing:0.08em;text-transform:uppercase;margin-bottom:6px}
  .sv{font-family:'DM Mono',monospace;font-size:20px;font-weight:700;margin-bottom:4px}
  .ss{font-size:11px;color:#94a3b8}
  .income{border-top:3px solid #10b981} .expense{border-top:3px solid #ef4444} .balance{border-top:3px solid #3b82f6} .count{border-top:3px solid #8b5cf6}
  .income .sv{color:#10b981} .expense .sv{color:#ef4444} .balance .sv{color:#3b82f6} .count .sv{color:#8b5cf6}
  .sr{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-bottom:28px}
  .stc{border-radius:10px;padding:14px 18px;display:flex;align-items:center;gap:14px}
  .stc.approved{background:#d1fae5} .stc.pending{background:#fef3c7} .stc.rejected{background:#fee2e2}
  .si{width:40px;height:40px;border-radius:10px;display:flex;align-items:center;justify-content:center;font-size:18px}
  .approved .si{background:#059669} .pending .si{background:#d97706} .rejected .si{background:#dc2626}
  .stl{font-size:11px;font-weight:700;color:#374151} .approved .stv{color:#065f46} .pending .stv{color:#92400e} .rejected .stv{color:#991b1b}
  .stv{font-family:'DM Mono',monospace;font-size:18px;font-weight:700} .sta{font-size:11px;color:#6b7280;margin-top:2px}
  .sh{display:flex;align-items:center;gap:12px;margin-bottom:14px}
  .st{font-family:'DM Serif Display',serif;font-size:18px;font-weight:400;color:#0f172a}
  .sb{background:#f1f5f9;color:#64748b;font-size:11px;font-weight:700;padding:2px 10px;border-radius:12px}
  .tw{background:#fff;border-radius:12px;border:1px solid #e2e8f0;overflow:hidden;margin-bottom:28px;box-shadow:0 1px 6px rgba(0,0,0,0.06)}
  table{width:100%;border-collapse:collapse} thead tr{background:#0f172a}
  th{padding:10px 14px;text-align:left;color:#94a3b8;font-size:10px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;white-space:nowrap}
  td{padding:11px 14px;border-bottom:1px solid #f1f5f9;vertical-align:middle}
  tr:last-child td{border-bottom:none} tfoot tr{background:#0f172a}
  tfoot td{padding:12px 14px;color:#e2e8f0;font-weight:700;font-size:13px;border-bottom:none}
  .tl{color:#94a3b8;font-size:11px}
  .rf{border-top:1px solid #e2e8f0;padding-top:20px;display:flex;justify-content:space-between;align-items:center;color:#94a3b8;font-size:11px}
  .pb{background:#1e293b;padding:12px 40px;display:flex;gap:12px;align-items:center;justify-content:flex-end;position:sticky;top:0;z-index:100}
  .btp{background:#3b82f6;color:white;border:none;padding:9px 22px;border-radius:8px;font-size:13px;font-weight:700;cursor:pointer;font-family:'DM Sans',sans-serif}
  .btc{background:#334155;color:#94a3b8;border:none;padding:9px 18px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;font-family:'DM Sans',sans-serif}
  .ph{color:#64748b;font-size:12px;margin-right:auto}
  @media print{body{background:white}.page{padding:20px}.pb{display:none!important}.tw{box-shadow:none}.rh,.stc,.sc,thead tr,tfoot tr{-webkit-print-color-adjust:exact;print-color-adjust:exact}tr{page-break-inside:avoid}}
  </style></head><body>
  <div class="pb"><span class="ph">💡 To save as PDF: Click Print → Change destination to "Save as PDF"</span><button class="btc" onclick="window.close()">✕ Close</button><button class="btp" onclick="window.print()">🖨 Print / Save PDF</button></div>
  <div class="page">
  <div class="rh"><div><div class="brand"><div class="bd">🏥</div><span class="bn">Medical ERP — Finance Department</span></div><h1 class="rt">${reportTitle}</h1><div class="rm">Generated: <span>${generatedOn} IST</span><br/>Records: <span>${expenses.length} entries</span><br/>Period: <span>${MONTH_NAMES[viewMonth]} ${viewYear}</span>${filterLines.length>0?`<br/>Filters: <span>${filterLines.join(' · ')}</span>`:''}</div></div><div style="text-align:right"><div class="rb">TOTAL EXPENSES<span class="ta">${totalAmount.toLocaleString('en-IN',{style:'currency',currency:'INR',minimumFractionDigits:2})}</span></div></div></div>
  <div class="sg">
    <div class="sc income"><div class="sl">Total Income</div><div class="sv">${parseFloat(summary?.total_income||0).toLocaleString('en-IN',{style:'currency',currency:'INR',minimumFractionDigits:0})}</div><div class="ss">All tuition fees received</div></div>
    <div class="sc expense"><div class="sl">Total Expenses</div><div class="sv">${parseFloat(summary?.total_expenses||0).toLocaleString('en-IN',{style:'currency',currency:'INR',minimumFractionDigits:0})}</div><div class="ss">All approved costs</div></div>
    <div class="sc balance"><div class="sl">Available Balance</div><div class="sv">${parseFloat(summary?.remaining_balance||0).toLocaleString('en-IN',{style:'currency',currency:'INR',minimumFractionDigits:0})}</div><div class="ss">Income minus expenses</div></div>
    <div class="sc count"><div class="sl">Records</div><div class="sv">${expenses.length}</div><div class="ss">${MONTH_NAMES[viewMonth]} ${viewYear}</div></div>
  </div>
  <div class="sh"><h2 class="st">Category Breakdown</h2><span class="sb">${Object.keys(catMap).length} categories</span></div>
  <div class="tw" style="margin-bottom:32px"><table><thead><tr><th>Category</th><th style="text-align:right">Entries</th><th style="text-align:right">Total</th><th style="text-align:right">Share</th></tr></thead><tbody>${catRows||'<tr><td colspan="4" style="text-align:center;padding:24px;color:#94a3b8">No data</td></tr>'}</tbody><tfoot><tr><td><span class="tl">GRAND TOTAL</span></td><td style="text-align:right">${expenses.length}</td><td style="text-align:right;font-family:monospace">${totalAmount.toLocaleString('en-IN',{style:'currency',currency:'INR',minimumFractionDigits:2})}</td><td style="text-align:right">100%</td></tr></tfoot></table></div>
  <div class="sh"><h2 class="st">Detailed Expense Ledger</h2><span class="sb">${expenses.length} records</span></div>
  <div class="tw"><table><thead><tr><th style="text-align:center">#</th><th>Date</th><th>Category</th><th>Description / Vendor</th><th>Invoice No.</th><th>Payment</th><th style="text-align:right">Amount</th><th>Recorded By</th><th>Notes</th></tr></thead><tbody>${expRows||'<tr><td colspan="10" style="text-align:center;padding:40px;color:#94a3b8">No records found.</td></tr>'}</tbody><tfoot><tr><td colspan="6" style="text-align:right"><span class="tl">TOTAL (${expenses.length})</span></td><td style="text-align:right;font-family:monospace;color:#fca5a5;font-size:15px">${totalAmount.toLocaleString('en-IN',{style:'currency',currency:'INR',minimumFractionDigits:2})}</td><td colspan="3"></td></tr></tfoot></table></div>
  <div class="rf"><div><strong>Medical ERP — Finance Department</strong><br/>Confidential · For Internal Use Only</div><div style="text-align:right">Generated: ${generatedOn} IST<br/>Period: ${MONTH_NAMES[viewMonth]} ${viewYear}</div></div>
  </div></body></html>`;

  const blob=new Blob([html],{type:'text/html;charset=utf-8'});
  const url=URL.createObjectURL(blob);
  const win=window.open(url,'_blank');
  if(!win) notify.info('Please allow pop-ups to view the report.');
  setTimeout(()=>URL.revokeObjectURL(url),60000);
};

/* ═══════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════ */
const DailyExpensesTab = () => {
  const [expenses,setExpenses]       = useState([]);
  const [summary,setSummary]         = useState(null);
  const [categoryStats,setCatStats]  = useState([]);
  const [loading,setLoading]         = useState(true);
  const [submitting,setSubmitting]   = useState(false);
  const [error,setError]             = useState('');
  const [success,setSuccess]         = useState('');
  const [search,setSearch]           = useState('');
  const [catFilter,setCatFilter]     = useState('');
  const [statusFilter,setStatusFilter] = useState('APPROVED');
  const [startDate,setStartDate]     = useState('');
  const [endDate,setEndDate]         = useState('');
  const [viewMonth,setViewMonth]     = useState(localNow.getMonth()+1);
  const [viewYear,setViewYear]       = useState(localNow.getFullYear());
  const [showAddModal,setShowAddModal]           = useState(false);
  const [showInvoiceModal,setShowInvoiceModal]   = useState(false);
  const [showDeleteConfirm,setShowDeleteConfirm] = useState(false);
  const [viewingExpense,setViewingExpense]       = useState(null);
  const [deletingId,setDeletingId]              = useState(null);
  const [activeView,setActiveView]              = useState('table');

  const emptyForm = {
    date:getLocalDateString(), category:'ELECTRICITY', description:'', amount:'',
    invoice_number:'', vendor:'', payment_method:'CASH', notes:'', status:'APPROVED',
  };
  const [form,setForm]   = useState(emptyForm);
  const [invoice,setInvoice] = useState(null);
  const fileRef = useRef();

  const loadAll = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = { status:statusFilter, month:viewMonth, year:viewYear };
      if(catFilter) params.category   = catFilter;
      if(startDate) params.start_date = startDate;
      if(endDate)   params.end_date   = endDate;
      const [expData,sumData,catData] = await Promise.all([
        expenseAPI.getExpenses(params),
        expenseAPI.getExpenseSummary(viewMonth,viewYear),
        expenseAPI.getCategoryStats(),
      ]);
      setExpenses(Array.isArray(expData)?expData:expData.results||[]);
      setSummary(sumData); setCatStats(catData);
    } catch(e) { console.error(e); setError('Failed to load expense data.'); }
    finally { setLoading(false); }
  },[statusFilter,catFilter,startDate,endDate,viewMonth,viewYear]);

  useEffect(()=>{ loadAll(); },[loadAll]);

  const filtered = expenses.filter(ex=>{
    if(!search) return true;
    const q=search.toLowerCase();
    return ex.description?.toLowerCase().includes(q)||ex.vendor?.toLowerCase().includes(q)||
      ex.invoice_number?.toLowerCase().includes(q)||ex.category_display?.toLowerCase().includes(q);
  });

  const availableBalance    = parseFloat(summary?.remaining_balance||0);
  const enteredAmt          = parseFloat(form.amount||0);
  const amountExceeds       = form.amount && enteredAmt > availableBalance;
  const noBalance           = availableBalance <= 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if(!form.amount||enteredAmt<=0){ setError('Please enter a valid amount.'); return; }
    if(noBalance){ setError(`❌ No available balance (₹${availableBalance.toLocaleString('en-IN',{minimumFractionDigits:2})}). Cannot record expense.`); return; }
    if(amountExceeds){ setError(`❌ Amount ₹${enteredAmt.toLocaleString('en-IN',{minimumFractionDigits:2})} exceeds available balance of ₹${availableBalance.toLocaleString('en-IN',{minimumFractionDigits:2})}.`); return; }
    setSubmitting(true); setError('');
    try {
      const fd=new FormData();
      Object.entries(form).forEach(([k,v])=>{ if(v!=='') fd.append(k,v); });
      if(invoice) fd.append('invoice',invoice);
      await expenseAPI.createExpense(fd);
      setSuccess('Expense recorded successfully.');
      setShowAddModal(false); setForm(emptyForm); setInvoice(null);
      loadAll(); setTimeout(()=>setSuccess(''),4000);
    } catch(e) {
      setError(e.response?.data?.error||JSON.stringify(e.response?.data)||'Failed to save expense.');
    } finally { setSubmitting(false); }
  };

  const handleDelete = async () => {
    if(!deletingId) return;
    try {
      await expenseAPI.deleteExpense(deletingId);
      setSuccess('Expense deleted.'); setShowDeleteConfirm(false); setDeletingId(null);
      loadAll(); setTimeout(()=>setSuccess(''),3000);
    } catch { setError('Failed to delete expense.'); }
  };

  const handleDownloadReport = () => {
    if(filtered.length===0){ setError('No records to export.'); return; }
    generateReport({ expenses:filtered, summary, categoryStats, viewMonth, viewYear,
      filters:{catFilter,statusFilter,startDate,endDate,search} });
  };

  const filteredTotal = filtered.reduce((s,e)=>s+parseFloat(e.amount||0),0);

  return (
    <div style={{fontFamily:"'DM Sans',sans-serif",color:'#1e293b'}}>
      <style>{CSS}</style>

      {error   && <Alert type="error"   msg={error}   onClose={()=>setError('')} />}
      {success && <Alert type="success" msg={success} onClose={()=>setSuccess('')} />}

      {/* Summary */}
      <SummaryStrip summary={summary} viewMonth={viewMonth} viewYear={viewYear}
        setViewMonth={setViewMonth} setViewYear={setViewYear} />

      {/* Header */}
      <div className="exp-header-row">
        <div>
          <h2 style={{fontFamily:"'DM Serif Display',serif",fontSize:22,fontWeight:400,color:'#0f172a',margin:0}}>
            Daily Expense Ledger
          </h2>
          <p style={{fontSize:12,color:'#94a3b8',margin:'4px 0 0'}}>All operational expenditure — Finance access only</p>
        </div>
        <div className="exp-header-actions">
          <button style={{...S.btn,...S.btnOutline}} onClick={()=>setActiveView(v=>v==='table'?'analytics':'table')}>
            {activeView==='table'?'📊 Analytics':'📋 Table'}
          </button>
          <button style={{...S.btn,...S.btnDownload}} className="btn-hover" onClick={handleDownloadReport}>
            ⬇ Download Report
          </button>
          <button style={{...S.btn,...S.btnPrimary}} className="btn-hover"
            onClick={()=>{ setForm(emptyForm); setInvoice(null); setShowAddModal(true); }}>
            + Record Expense
          </button>
        </div>
      </div>

      {/* Analytics */}
      {activeView==='analytics' && <CategoryAnalytics categoryStats={categoryStats} fmt={fmt} />}

      {/* Table view */}
      {activeView==='table' && (
        <>
          {/* Filters */}
          <div className="exp-filter-bar">
            <input className="exp-filter-input" placeholder="🔍 Search description, vendor, invoice…"
              value={search} onChange={e=>setSearch(e.target.value)} />
            <select className="exp-filter-select" value={catFilter} onChange={e=>setCatFilter(e.target.value)}>
              <option value="">All Categories</option>
              {Object.entries(CATEGORY_META).map(([k,v])=><option key={k} value={k}>{v.icon} {v.label}</option>)}
            </select>

            <input type="date" className="exp-filter-input" value={startDate}
              onChange={e=>setStartDate(e.target.value)} style={{minWidth:130,flex:'none'}} />
            <input type="date" className="exp-filter-input" value={endDate}
              onChange={e=>setEndDate(e.target.value)} style={{minWidth:130,flex:'none'}} />
            {(catFilter||startDate||endDate||search||statusFilter!=='APPROVED')&&(
              <button style={{...S.btn,...S.btnOutline,color:'#ef4444',borderColor:'#ef4444'}}
                onClick={()=>{ setSearch('');setCatFilter('');setStartDate('');setEndDate('');setStatusFilter('APPROVED'); }}>
                ✕ Clear
              </button>
            )}
          </div>

          {/* Filtered total */}
          <div style={{fontSize:12,color:'#64748b',marginBottom:12,display:'flex',alignItems:'center',flexWrap:'wrap',gap:8}}>
            Showing <strong style={{margin:'0 3px'}}>{filtered.length}</strong> records &nbsp;·&nbsp;
            Filtered Total: <span style={{fontFamily:'DM Mono,monospace',color:'#dc2626',fontWeight:700}}>{fmt(filteredTotal)}</span>
            {filtered.length>0&&(
              <button style={{...S.btn,...S.btnDownloadSm}} className="btn-hover" onClick={handleDownloadReport}>
                ⬇ Export {filtered.length} records
              </button>
            )}
          </div>

          {loading ? <Skeleton /> : filtered.length===0 ? <EmptyState onAdd={()=>setShowAddModal(true)} /> : (
            <>
              {/* ── Desktop table ── */}
              <div className="exp-table-wrap">
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                  <thead>
                    <tr style={{background:'#0f172a'}}>
                      {['Date','Category','Description / Vendor','Inv. No.','Payment','Amount','Invoice','Actions'].map(h=>(
                        <th key={h} style={{padding:'11px 14px',color:'#94a3b8',fontSize:11,fontWeight:700,
                          letterSpacing:'0.07em',textTransform:'uppercase',whiteSpace:'nowrap',
                          textAlign:h==='Amount'?'right':'left'}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((ex,i)=>{
                      const meta=CATEGORY_META[ex.category]||{};
                      return (
                        <tr key={ex.id} className="exp-row fade-in" style={{animationDelay:`${i*0.03}s`}}>
                          <td style={S.td}><span style={{fontFamily:'DM Mono,monospace',fontSize:13}}>{fmtDate(ex.date)}</span></td>
                          <td style={S.td}>
                            <span style={{display:'inline-flex',alignItems:'center',gap:5,background:meta.color+'18',color:meta.color,padding:'3px 10px',borderRadius:6,fontSize:12,fontWeight:600}}>
                              {meta.icon} {ex.category_display}
                            </span>
                          </td>
                          <td style={{...S.td,maxWidth:220}}>
                            <div style={{fontWeight:600,fontSize:13,color:'#1e293b',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{ex.description}</div>
                            {ex.vendor&&<div style={{fontSize:11,color:'#94a3b8',marginTop:1}}>🏢 {ex.vendor}</div>}
                          </td>
                          <td style={S.td}><span style={{fontFamily:'DM Mono,monospace',fontSize:12,color:'#475569'}}>{ex.invoice_number||'—'}</span></td>
                          <td style={S.td}><span style={{fontSize:12,color:'#475569'}}>{ex.payment_method_display}</span></td>
                          <td style={{...S.td,textAlign:'right'}}>
                            <span style={{fontFamily:'DM Mono,monospace',fontWeight:700,fontSize:14,color:'#dc2626'}}>{fmt(ex.amount)}</span>
                          </td>

                          <td style={{...S.td,textAlign:'center'}}>
                            {ex.has_invoice
                              ? <button style={{...S.iconBtn,color:'#3b82f6'}} className="btn-hover" title="View Invoice" onClick={()=>{ setViewingExpense(ex);setShowInvoiceModal(true); }}>📄</button>
                              : <span style={{color:'#cbd5e1'}}>—</span>}
                          </td>
                          <td style={S.td}>
                            <button style={{...S.iconBtn,color:'#dc2626'}} className="btn-hover"
                              onClick={()=>{ setDeletingId(ex.id);setShowDeleteConfirm(true); }}>🗑</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{background:'#0f172a'}}>
                      <td colSpan={5} style={{padding:'11px 14px',color:'#94a3b8',fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.06em'}}>
                        Total ({filtered.length} records)
                      </td>
                      <td style={{padding:'11px 14px',textAlign:'right',fontFamily:'DM Mono,monospace',fontWeight:700,fontSize:15,color:'#fca5a5'}}>
                        {fmt(filteredTotal)}
                      </td>
                      <td colSpan={3}/>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* ── Mobile cards ── */}
              <div className="exp-mobile-cards">
                {filtered.map(ex=>{
                  const meta=CATEGORY_META[ex.category]||{};
                  return (
                    <div key={ex.id} className="exp-mobile-card fade-in">
                      <div className="exp-mobile-card-header">
                        <div style={{flex:1,minWidth:0}}>
                          <span style={{display:'inline-flex',alignItems:'center',gap:4,background:meta.color+'18',color:meta.color,padding:'3px 10px',borderRadius:6,fontSize:11,fontWeight:700,marginBottom:6}}>
                            {meta.icon} {ex.category_display}
                          </span>
                          <div style={{fontWeight:700,fontSize:14,color:'#1e293b',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{ex.description}</div>
                          {ex.vendor&&<div style={{fontSize:11,color:'#94a3b8',marginTop:2}}>🏢 {ex.vendor}</div>}
                        </div>
                        <div style={{textAlign:'right',flexShrink:0}}>
                          <div style={{fontFamily:'DM Mono,monospace',fontWeight:700,fontSize:16,color:'#dc2626'}}>{fmt(ex.amount)}</div>
                          <div style={{marginTop:4}}>{statusPill(ex.status)}</div>
                        </div>
                      </div>
                      <div className="exp-mobile-card-body">
                        <div>
                          <div className="exp-mobile-field-label">Date</div>
                          <div className="exp-mobile-field-value" style={{fontFamily:'DM Mono,monospace',fontSize:12}}>{fmtDate(ex.date)}</div>
                        </div>
                        <div>
                          <div className="exp-mobile-field-label">Payment</div>
                          <div className="exp-mobile-field-value">{ex.payment_method_display||'—'}</div>
                        </div>
                        <div>
                          <div className="exp-mobile-field-label">Invoice No.</div>
                          <div className="exp-mobile-field-value" style={{fontFamily:'DM Mono,monospace',fontSize:12}}>{ex.invoice_number||'—'}</div>
                        </div>
                        <div>
                          <div className="exp-mobile-field-label">Recorded By</div>
                          <div className="exp-mobile-field-value">{ex.created_by_name||'—'}</div>
                        </div>
                        {ex.notes&&(
                          <div style={{gridColumn:'1/-1'}}>
                            <div className="exp-mobile-field-label">Notes</div>
                            <div className="exp-mobile-field-value" style={{fontSize:12,color:'#64748b'}}>{ex.notes}</div>
                          </div>
                        )}
                      </div>
                      <div className="exp-mobile-card-footer">
                        {ex.has_invoice
                          ? <button style={{...S.btn,...S.btnOutline,fontSize:12,padding:'6px 12px'}} onClick={()=>{ setViewingExpense(ex);setShowInvoiceModal(true); }}>📄 View Invoice</button>
                          : <span style={{fontSize:12,color:'#94a3b8'}}>No invoice attached</span>}
                        <button style={{...S.btn,background:'#fee2e2',color:'#dc2626',border:'1px solid #fca5a5',fontSize:12,padding:'6px 12px'}}
                          onClick={()=>{ setDeletingId(ex.id);setShowDeleteConfirm(true); }}>🗑 Delete</button>
                      </div>
                    </div>
                  );
                })}
                {/* Mobile total */}
                <div style={{background:'#0f172a',borderRadius:10,padding:'14px 16px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <span style={{color:'#94a3b8',fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.06em'}}>Total ({filtered.length} records)</span>
                  <span style={{fontFamily:'DM Mono,monospace',fontWeight:700,fontSize:16,color:'#fca5a5'}}>{fmt(filteredTotal)}</span>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* Add modal */}
      {showAddModal&&(
        <Modal title="Record New Expense" onClose={()=>setShowAddModal(false)} width={620}>
          <form onSubmit={handleSubmit}>
            <div className="exp-form-row">
              <FormField label="Date *" style={{flex:1}}>
                <input type="date" style={S.input} required value={form.date} onChange={e=>setForm({...form,date:e.target.value})}/>
              </FormField>
              <FormField label="Category *" style={{flex:1.4}}>
                <select style={S.input} value={form.category} onChange={e=>setForm({...form,category:e.target.value})}>
                  {Object.entries(CATEGORY_META).map(([k,v])=><option key={k} value={k}>{v.icon} {v.label}</option>)}
                </select>
              </FormField>
            </div>
            <FormField label="Description *">
              <textarea style={{...S.input,height:70,resize:'vertical'}} required placeholder="Brief description of the expense…"
                value={form.description} onChange={e=>setForm({...form,description:e.target.value})}/>
            </FormField>
            <div className="exp-form-row">
              <FormField label="Amount (₹) *" style={{flex:1}}>
                <input type="number" min="0.01" step="0.01"
                  style={{...S.input,borderColor:amountExceeds?'#ef4444':'#e2e8f0'}}
                  required placeholder="0.00" value={form.amount}
                  onChange={e=>setForm({...form,amount:e.target.value})}/>
                {noBalance&&(
                  <div style={S.balWarn}>🚫 No available balance. Cannot record expenses.</div>
                )}
                {amountExceeds&&!noBalance&&(
                  <div style={S.balWarn}>⚠️ Exceeds available balance of <strong>{fmt(availableBalance)}</strong></div>
                )}
              </FormField>
              <FormField label="Payment Method" style={{flex:1}}>
                <select style={S.input} value={form.payment_method} onChange={e=>setForm({...form,payment_method:e.target.value})}>
                  {PAYMENT_METHODS.map(m=><option key={m} value={m}>{m.replace('_',' ')}</option>)}
                </select>
              </FormField>
            </div>
            <div className="exp-form-row">
              <FormField label="Vendor / Supplier" style={{flex:1}}>
                <input style={S.input} placeholder="Vendor name" value={form.vendor} onChange={e=>setForm({...form,vendor:e.target.value})}/>
              </FormField>
              <FormField label="Invoice Number" style={{flex:1}}>
                <input style={S.input} placeholder="INV-2026-001" value={form.invoice_number} onChange={e=>setForm({...form,invoice_number:e.target.value})}/>
              </FormField>
            </div>
            <FormField label="Attach Invoice / Bill">
              <div style={S.dropzone} onClick={()=>fileRef.current?.click()}
                onDragOver={e=>e.preventDefault()} onDrop={e=>{ e.preventDefault();setInvoice(e.dataTransfer.files[0]); }}>
                <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{display:'none'}} onChange={e=>setInvoice(e.target.files[0])}/>
                {invoice?(
                  <div style={{display:'flex',alignItems:'center',gap:8}}>
                    <span style={{fontSize:24}}>📎</span>
                    <div><div style={{fontWeight:600,fontSize:13}}>{invoice.name}</div><div style={{fontSize:11,color:'#94a3b8'}}>{(invoice.size/1024).toFixed(1)} KB</div></div>
                    <button type="button" style={{marginLeft:'auto',color:'#ef4444',background:'none',border:'none',cursor:'pointer',fontSize:18}} onClick={e=>{ e.stopPropagation();setInvoice(null); }}>×</button>
                  </div>
                ):(
                  <div style={{textAlign:'center',color:'#94a3b8'}}>
                    <div style={{fontSize:28,marginBottom:4}}>📤</div>
                    <div style={{fontSize:13}}>Drop file here or <span style={{color:'#3b82f6'}}>browse</span></div>
                    <div style={{fontSize:11,marginTop:2}}>PDF, JPG, PNG — max 10 MB</div>
                  </div>
                )}
              </div>
            </FormField>
            <FormField label="Notes (optional)">
              <textarea style={{...S.input,height:56,resize:'vertical'}} placeholder="Any additional remarks…"
                value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})}/>
            </FormField>
            <div style={S.modalFooter}>
              <button type="button" style={{...S.btn,...S.btnOutline}} onClick={()=>setShowAddModal(false)}>Cancel</button>
              <button type="submit"
                style={{...S.btn,...S.btnPrimary,opacity:(submitting||amountExceeds||noBalance)?0.5:1,cursor:(submitting||amountExceeds||noBalance)?'not-allowed':'pointer'}}
                disabled={submitting||amountExceeds||noBalance}>
                {submitting?<><span className="spin">⟳</span> Saving…</>:'✓ Record Expense'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Invoice modal */}
      {showInvoiceModal&&viewingExpense&&(
        <Modal title={`Invoice — ${viewingExpense.category_display}`} width={800}
          onClose={()=>{ setShowInvoiceModal(false);setViewingExpense(null); }}>
          <InvoiceViewer expense={viewingExpense} fmt={fmt} fmtDate={fmtDate}/>
        </Modal>
      )}

      {/* Delete confirm */}
      {showDeleteConfirm&&(
        <Modal title="Confirm Delete" width={420} onClose={()=>{ setShowDeleteConfirm(false);setDeletingId(null); }}>
          <p style={{color:'#475569',lineHeight:1.6}}>This expense will be permanently removed. This action cannot be undone.</p>
          <div style={S.modalFooter}>
            <button style={{...S.btn,...S.btnOutline}} onClick={()=>{ setShowDeleteConfirm(false);setDeletingId(null); }}>Cancel</button>
            <button style={{...S.btn,background:'#dc2626',color:'#fff',border:'none'}} className="btn-hover" onClick={handleDelete}>Delete Expense</button>
          </div>
        </Modal>
      )}
    </div>
  );
};

/* ── Summary Strip ── */
const SummaryStrip = ({ summary, viewMonth, viewYear, setViewMonth, setViewYear }) => {
  if(!summary) return (
    <div className="exp-summary-strip">
      {[...Array(4)].map((_,i)=><div key={i} style={{background:'#f1f5f9',borderRadius:12,height:120}}/>)}
    </div>
  );
  return (
    <div className="exp-summary-strip" style={{ marginBottom: 20 }}>
      {/* Month filter */}
      <div style={{ background: '#fff', borderRadius: 12, padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', border: '1px solid #f1f5f9', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.05em' }}>📅 SELECT MONTH & YEAR:</div>
          <div className="exp-month-row" style={{ marginBottom: 0 }}>
            <select style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, background: '#fff', fontWeight: 700, color: '#1d4ed8' }}
              value={viewMonth} onChange={e=>setViewMonth(+e.target.value)}>
              {MONTH_NAMES.slice(1).map((m,i)=><option key={i+1} value={i+1}>{m}</option>)}
            </select>
            <select style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, background: '#fff', fontWeight: 700, color: '#1d4ed8' }}
              value={viewYear} onChange={e=>setViewYear(+e.target.value)}>
              {[2024,2025,2026,2027].map(y=><option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 20, alignItems: 'center', fontSize: 12, color: '#475569' }}>
          <div>
            Month Approved Expenses: <span style={{ fontFamily: 'DM Mono,monospace', fontWeight: 800, color: '#dc2626', fontSize: 14 }}>₹{parseFloat(summary?.expenses_this_month||0).toLocaleString('en-IN')}</span>
          </div>
          <div>
            Today: <span style={{ fontFamily: 'DM Mono,monospace', fontWeight: 800, color: '#7c3aed', fontSize: 14 }}>₹{parseFloat(summary?.expenses_today||0).toLocaleString('en-IN')}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const SCard = ({ icon, label, sub, value, accent, badge, highlight }) => (
  <div style={{background:highlight?accent+'08':'#fff',borderRadius:12,padding:'18px 20px',boxShadow:'0 1px 4px rgba(0,0,0,0.07)',border:'1px solid #f1f5f9',borderTop:`3px solid ${accent}`}}>
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:8}}>
      <span style={{fontSize:28}}>{icon}</span>
      <span style={{fontSize:9,fontWeight:800,letterSpacing:'0.1em',background:accent+'20',color:accent,padding:'2px 8px',borderRadius:4}}>{badge}</span>
    </div>
    <div style={{fontFamily:'DM Mono,monospace',fontSize:22,fontWeight:700,color:accent,marginBottom:4}}>{value}</div>
    <div style={{fontSize:12,fontWeight:600,color:'#1e293b'}}>{label}</div>
    <div style={{fontSize:11,color:'#94a3b8',marginTop:2}}>{sub}</div>
  </div>
);

/* ── Category Analytics ── */
const CategoryAnalytics = ({ categoryStats, fmt }) => {
  const max = Math.max(...categoryStats.map(c=>parseFloat(c.total||0)),1);
  return (
    <div style={{background:'#fff',borderRadius:12,padding:20,boxShadow:'0 1px 4px rgba(0,0,0,0.07)',border:'1px solid #f1f5f9',marginBottom:16}} className="fade-in">
      <h3 style={{fontSize:15,fontWeight:700,color:'#1e293b',marginBottom:16,fontFamily:"'DM Serif Display',serif"}}>Expense Breakdown by Category <span style={{fontSize:12,fontWeight:400,color:'#94a3b8'}}>(All-Time, Approved Only)</span></h3>
      {categoryStats.length===0?<p style={{color:'#94a3b8',textAlign:'center',padding:'32px 0'}}>No data to display.</p>:(
        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          {categoryStats.map(c=>{
            const meta=CATEGORY_META[c.category]||{};
            const pct=(parseFloat(c.total)/max)*100;
            return (
              <div key={c.category}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:5,flexWrap:'wrap',gap:4}}>
                  <span style={{fontSize:13,fontWeight:600,color:'#374151'}}>{meta.icon} {c.category_display}</span>
                  <span style={{fontFamily:'DM Mono,monospace',fontSize:13,fontWeight:700,color:'#dc2626'}}>
                    {fmt(c.total)}<span style={{fontSize:11,color:'#94a3b8',marginLeft:6}}>({c.count} entries)</span>
                  </span>
                </div>
                <div style={{height:8,background:'#f1f5f9',borderRadius:4,overflow:'hidden'}}>
                  <div style={{height:'100%',borderRadius:4,background:`linear-gradient(90deg,${meta.color}cc,${meta.color})`,width:`${pct}%`,transition:'width 0.8s cubic-bezier(.4,0,.2,1)'}}/>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ── Invoice Viewer ── */
const InvoiceViewer = ({ expense, fmt, fmtDate }) => {
  const meta=CATEGORY_META[expense.category]||{};
  const isPdf=expense.invoice_url?.toLowerCase().endsWith('.pdf');
  return (
    <div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:'10px 24px',background:'#f8fafc',borderRadius:10,padding:16,marginBottom:16}}>
        {[['Category',`${meta.icon} ${expense.category_display}`],['Date',fmtDate(expense.date)],['Amount',fmt(expense.amount)],['Vendor',expense.vendor||'—'],['Invoice No.',expense.invoice_number||'—'],['Payment',expense.payment_method_display],['Status',expense.status],['Recorded By',expense.created_by_name]].map(([k,v])=>(
          <div key={k}>
            <div style={{fontSize:10,fontWeight:700,color:'#94a3b8',letterSpacing:'0.07em',textTransform:'uppercase'}}>{k}</div>
            <div style={{fontSize:13,fontWeight:600,color:'#1e293b',marginTop:2}}>{v}</div>
          </div>
        ))}
      </div>
      {expense.description&&<div style={{marginBottom:16}}><div style={{fontSize:10,fontWeight:700,color:'#94a3b8',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:4}}>Description</div><p style={{color:'#374151',fontSize:13,lineHeight:1.7}}>{expense.description}</p></div>}
      {expense.notes&&<div style={{marginBottom:16}}><div style={{fontSize:10,fontWeight:700,color:'#94a3b8',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:4}}>Notes</div><p style={{color:'#374151',fontSize:13,lineHeight:1.7}}>{expense.notes}</p></div>}
      {expense.invoice_url&&(
        <div style={{border:'1px solid #e2e8f0',borderRadius:10,overflow:'hidden',marginBottom:12}}>
          <div style={{background:'#1e293b',padding:'10px 16px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <span style={{color:'#e2e8f0',fontSize:13,fontWeight:600}}>📎 Invoice / Bill</span>
            <a href={expense.invoice_url} target="_blank" rel="noreferrer" style={{color:'#60a5fa',fontSize:12,textDecoration:'none',fontWeight:600}}>↗ Open in new tab</a>
          </div>
          {isPdf?<iframe src={expense.invoice_url} style={{width:'100%',height:460,border:'none'}} title="Invoice"/>
            :<img src={expense.invoice_url} alt="Invoice" style={{width:'100%',maxHeight:460,objectFit:'contain',background:'#f8fafc'}}/>}
        </div>
      )}
    </div>
  );
};

/* ── Modal ── */
const Modal = ({ title, children, onClose, width=560 }) => (
  <div style={{position:'fixed',inset:0,background:'rgba(15,23,42,0.55)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:16}} onClick={onClose}>
    <div className="exp-modal-box slide-up" style={{maxWidth:width}} onClick={e=>e.stopPropagation()}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'18px 24px',borderBottom:'1px solid #f1f5f9',background:'#0f172a'}}>
        <h3 style={{fontFamily:"'DM Serif Display',serif",fontSize:18,fontWeight:400,color:'#f8fafc',margin:0}}>{title}</h3>
        <button style={{background:'none',border:'none',color:'#94a3b8',fontSize:26,cursor:'pointer',lineHeight:1,padding:0}} onClick={onClose}>×</button>
      </div>
      <div style={{padding:24,overflowY:'auto',flex:1}}>{children}</div>
    </div>
  </div>
);

const FormField = ({ label, children, style={} }) => (
  <div style={{marginBottom:14,...style}}>
    <label style={{display:'block',fontSize:11,fontWeight:700,color:'#64748b',letterSpacing:'0.06em',textTransform:'uppercase',marginBottom:5}}>{label}</label>
    {children}
  </div>
);

const Alert = ({ type, msg, onClose }) => (
  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 16px',borderRadius:10,marginBottom:14,background:type==='error'?'#fee2e2':'#d1fae5',color:type==='error'?'#991b1b':'#065f46',border:`1px solid ${type==='error'?'#fca5a5':'#6ee7b7'}`,fontSize:13,fontWeight:500}} className="fade-in">
    <span style={{flex:1}}>{type==='error'?'⚠️ ':'✓ '}{msg}</span>
    <button style={{background:'none',border:'none',cursor:'pointer',fontSize:18,color:'inherit',flexShrink:0}} onClick={onClose}>×</button>
  </div>
);

const Skeleton = () => (
  <div style={{background:'#fff',borderRadius:12,padding:20,boxShadow:'0 1px 4px rgba(0,0,0,0.07)',border:'1px solid #f1f5f9'}}>
    {[...Array(6)].map((_,i)=>(
      <div key={i} style={{height:44,marginBottom:10,borderRadius:8,background:'linear-gradient(90deg,#f1f5f9 0%,#e2e8f0 50%,#f1f5f9 100%)',backgroundSize:'200% 100%',animation:'shimmer 1.4s infinite',opacity:1-i*0.1}}/>
    ))}
  </div>
);

const EmptyState = ({ onAdd }) => (
  <div style={{textAlign:'center',padding:'48px 24px',background:'#fff',borderRadius:14,border:'2px dashed #e2e8f0'}}>
    <div style={{fontSize:52,marginBottom:12}}>🧾</div>
    <div style={{fontSize:18,fontWeight:700,color:'#1e293b',marginBottom:6}}>No expenses found</div>
    <div style={{fontSize:13,color:'#94a3b8',marginBottom:24}}>No expense records match your current filters.</div>
    <button style={{...S.btn,...S.btnPrimary}} className="btn-hover" onClick={onAdd}>+ Record First Expense</button>
  </div>
);

const S = {
  btn:         {padding:'9px 18px',borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer',display:'inline-flex',alignItems:'center',gap:6,transition:'all 0.15s',border:'none'},
  btnPrimary:  {background:'#1e293b',color:'#fff',border:'1px solid #1e293b'},
  btnOutline:  {background:'#fff',color:'#475569',border:'1px solid #e2e8f0'},
  btnDownload: {background:'linear-gradient(135deg,#059669,#10b981)',color:'#fff',border:'none',boxShadow:'0 2px 8px rgba(5,150,105,0.3)'},
  btnDownloadSm:{background:'#f0fdf4',color:'#059669',border:'1px solid #a7f3d0',padding:'4px 12px',fontSize:11,fontWeight:700,borderRadius:6},
  iconBtn:     {background:'none',border:'none',cursor:'pointer',fontSize:16,padding:'4px 6px',borderRadius:6,transition:'all 0.15s'},
  td:          {padding:'12px 14px',borderBottom:'1px solid #f1f5f9',verticalAlign:'middle'},
  input:       {width:'100%',padding:'9px 12px',borderRadius:8,border:'1px solid #e2e8f0',fontSize:13,color:'#1e293b',background:'#fafafa',transition:'border 0.15s'},
  dropzone:    {border:'2px dashed #e2e8f0',borderRadius:10,padding:'20px 16px',cursor:'pointer',transition:'border 0.15s',background:'#f8fafc'},
  modalFooter: {display:'flex',justifyContent:'flex-end',gap:10,paddingTop:16,borderTop:'1px solid #f1f5f9',marginTop:4,flexWrap:'wrap'},
  balWarn:     {marginTop:6,padding:'7px 11px',borderRadius:7,background:'#fef2f2',border:'1px solid #fca5a5',color:'#991b1b',fontSize:11,fontWeight:600,lineHeight:1.5},
};

export default DailyExpensesTab;